import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { api_routes, db_tables } from '@app/consts';
import { ArticlesService } from '../../services/articles.service';
import { EmployeeArticle } from '../../models/article.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ArticleDialogComponent } from '../article-dialog/article-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-articles-display',
  templateUrl: './articles-display.component.html',
  styleUrls: ['./articles-display.component.scss']
})
export class ArticlesDisplayComponent implements OnInit {
  @Input() region: string;
  @Input() title: string;
  @ViewChild('viewport') viewport: ElementRef<HTMLElement>;

  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean = true;
  pageSize: number = 10;
  skip: number = 0;
  articles: EmployeeArticle[] = [];
  dialogRef: any;
  canScrollPrev = false;
  canScrollNext = false;
  total: number;
  articleImageUrls: Map<string, any> = new Map();
  clickedArticleIds: Set<string> = new Set();
  isTitleTruncated: boolean = false;

  constructor(
    private articlesService: ArticlesService,
    public translate: TranslateService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.getArticles();
  }

  getArticles() {
    this.isLoading = true;

    this.articlesService.getEmployeeArticles(this.region, this.pageSize, this.skip, null, null)
      .subscribe(
        {
          next: (v) => {
            this.articles = this.articles.concat(v.data);
            this.total = v.total;
            this.cacheArticleImageUrls(v.data);
            this.isLoading = false;
            // Wait for the new cards to render before measuring the track
            setTimeout(() => this.updateScrollState());
          },
          error: (e) => {
            this.isLoading = false;
          }
        }
      );
  }
  
  loadMoreArticles() {
    this.skip += this.pageSize;
    this.getArticles();
  }

  openArticleDialog(article: EmployeeArticle) {
    this.clickedArticleIds.add(article.id);
    
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      article: article
    };

    this.dialogRef = this.dialog.open(ArticleDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      sub.unsubscribe();
    });
  }

  cacheArticleImageUrls(articles: EmployeeArticle[]): void {
    articles.forEach(article => {
      if (article.titleImage && !this.articleImageUrls.has(article.id)) {
        const imageUrl = this.articlesService.getEmployeeArticleFile(article.id, article.titleImage.id);
        this.articleImageUrls.set(article.id, imageUrl);
      }
    });
  }

  getArticleImageUrl(article: EmployeeArticle): any {
    return this.articleImageUrls.get(article.id) || null;
  }

  scrollCards(direction: 1 | -1): void {
    const track = this.viewport?.nativeElement;
    track?.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
  }

  @HostListener('window:resize')
  onViewportScroll(): void {
    this.updateScrollState();

    const track = this.viewport?.nativeElement;
    const nearEnd = track && track.scrollLeft + track.clientWidth >= track.scrollWidth - track.clientWidth / 2;
    if (nearEnd && !this.isLoading && this.articles.length < this.total) {
      this.loadMoreArticles();
    }
  }

  private updateScrollState(): void {
    const track = this.viewport?.nativeElement;
    if (!track) {
      return;
    }
    this.canScrollPrev = track.scrollLeft > 1;
    this.canScrollNext = track.scrollLeft + track.clientWidth < track.scrollWidth - 1 || this.articles.length < this.total;
  }

  isArticleRead(article: EmployeeArticle): boolean {
    return (article.lastViewedOn !== null && article.lastViewedOn >= article.lastEditedOn) || this.clickedArticleIds.has(article.id);
  }

  shouldShowTooltip(event: MouseEvent): boolean {
    const element = event.target as HTMLElement;
    return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
  }

}
